import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';

const defaultGoal = {
  goalName: '',
  targetAmount: '',
  timeframe: '',
  strategy: 'moderate',
};

const strategies = {
  aggressive: { return: 12, risk: 'High', equity: '80-100%', debt: '0-20%' },
  moderate: { return: 10, risk: 'Medium', equity: '60-80%', debt: '20-40%' },
  safe: { return: 8, risk: 'Low', equity: '40-60%', debt: '40-60%' }
};

const Goals = () => {
  const { currentUser } = useAuth();
  const [goals, setGoals] = useState([]);
  const [form, setForm] = useState(defaultGoal);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch user's goals in real-time
  useEffect(() => {
    if (!currentUser?.uid) return;
    const q = collection(db, 'users', currentUser.uid, 'goals');
    const unsub = onSnapshot(q, (snap) => {
      setGoals(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, [currentUser]);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'users', currentUser.uid, 'goals', editingId), form);
      } else {
        await addDoc(collection(db, 'users', currentUser.uid, 'goals'), form);
      }
      setForm(defaultGoal);
      setEditingId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (goal) => {
    setForm(goal);
    setEditingId(goal.id);
  };

  const handleCancel = () => {
    setForm(defaultGoal);
    setEditingId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e0e7ff] via-white to-[#f0f5ff] py-8 flex items-center justify-center">
      <div className="w-full max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 px-8 py-8 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-1">Your Goals</h2>
          <p className="text-indigo-100 text-center max-w-md mx-auto">
            Add and manage your investment goals.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 sm:p-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <div>
              <label className="block text-sm font-semibold mb-1 text-gray-700">Goal Name</label>
              <input
                name="goalName"
                value={form.goalName}
                onChange={handleChange}
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                placeholder="e.g. Child Education"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1 text-gray-700">Target Amount</label>
              <input
                name="targetAmount"
                type="number"
                value={form.targetAmount}
                onChange={handleChange}
                required
                max={100000000}
                min={1000}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                placeholder="e.g. 1000000"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1 text-gray-700">Timeframe (years)</label>
              <input
                name="timeframe"
                type="number"
                value={form.timeframe}
                onChange={handleChange}
                required
                max={40}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                placeholder="e.g. 10"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1 text-gray-700">Strategy</label>
              <select
                name="strategy"
                value={form.strategy}
                onChange={handleChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                required
              >
                <option value="aggressive">Aggressive</option>
                <option value="moderate">Moderate</option>
                <option value="safe">Safe</option>
              </select>
            </div>
          </div>
          <div className="flex justify-center mt-10">
            <button
              type="submit"
              disabled={loading}
              className={`w-full sm:w-1/2 bg-gradient-to-r from-indigo-500 to-indigo-700 text-white py-3 rounded-xl font-semibold shadow-lg hover:from-indigo-600 hover:to-indigo-800 transition-all duration-200 ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {editingId ? 'Update Goal' : 'Add Goal'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={handleCancel}
                className="ml-4 px-4 py-3 rounded-xl font-semibold border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
        <div className="px-6 pb-8">
          <h3 className="text-xl font-bold text-indigo-700 mb-4">Your Goals</h3>
          {goals.length === 0 ? (
            <div className="text-gray-500">No goals added yet.</div>
          ) : (
            <div className="space-y-4">
              {goals.map(goal => (
                <div key={goal.id} className="bg-indigo-50 border-l-4 border-indigo-400 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <div className="font-semibold text-lg text-indigo-800">{goal.goalName}</div>
                    <div className="text-gray-700 text-sm">
                      Target: ₹{goal.targetAmount} | Timeframe: {goal.timeframe} years | Strategy: {goal.strategy}
                    </div>
                  </div>
                  <button
                    onClick={() => handleEdit(goal)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Goals;